import { useMemo, useState, useEffect } from 'react';
import { useLeaderboardParticipants } from '../../../../../hooks/useLeaderboardParticipants';
import { useLeaderboardDistricts } from '../../../../../hooks/useLeaderboardDistricts';
import { useLeaderboardOffices } from '../../../../../hooks/useLeaderboardOffices';
import { Participant } from '../../../types';

interface LeaderboardParticipant extends Participant {
  rank: number;
  area: string;
  avatar?: string;
}

const mapToViewModel = (p: any): LeaderboardParticipant => ({
  id: p.id,
  rank: p.rank,
  name: `${p.firstName} ${p.lastName}`,
  area: p.districtNames || '',
  position: p.rank,
});

export const useLeaderboardData = (
  eventId: number | undefined,
  startDate: Date | null,
  endDate: Date | null
) => {
  const [hasLoaded, setHasLoaded] = useState(false);
  const {
    participants,
    myParticipant,
    pageSize,
    totalPages: participantsTotalPages,
    pageNumber: participantsPage,
    setPageNumber: setParticipantsPage,
    isLoading: participantsLoading,
  } = useLeaderboardParticipants(eventId, undefined, startDate, endDate);
  const {
    districts,
    totalPages: districtsTotalPages,
    pageNumber: districtsPage,
    setPageNumber: setDistrictsPage,
    isLoading: districtsLoading,
  } = useLeaderboardDistricts(eventId, undefined, startDate, endDate);
  const {
    offices,
    totalPages: officesTotalPages,
    pageNumber: officesPage,
    setPageNumber: setOfficesPage,
    isLoading: officesLoading,
  } = useLeaderboardOffices(eventId, undefined, startDate, endDate);

  const participantRows = useMemo(() => (participants || []).map(mapToViewModel), [participants]);
  const areaRows = useMemo(
    () =>
      (districts || []).map((d) => ({
        id: d.id,
        rank: d.rank,
        area: d.districtName,
        score: d.totalScore,
      })),
    [districts]
  );
  const officeRows = useMemo(
    () =>
      (offices || []).map((o: any) => ({
        id: o.id,
        rank: o.rank,
        office: o.name,
        score: o.totalScore,
      })),
    [offices]
  );

  const isLoading = useMemo(() => {
    return participantsLoading || districtsLoading || officesLoading;
  }, [participantsLoading, districtsLoading, officesLoading]);

  useEffect(() => {
    if (!isLoading && (participants.length > 0 || districts.length > 0 || offices.length > 0)) {
      setHasLoaded(true);
    }
  }, [isLoading, participants.length, districts.length, offices.length]);

  const startDateTime = startDate?.getTime();
  const endDateTime = endDate?.getTime();

  useEffect(() => {
    setHasLoaded(false);
  }, [eventId, startDateTime, endDateTime]);

  return {
    participantRows,
    areaRows,
    officeRows,
    myParticipant,
    participants,
    pageSize,
    participantsTotalPages,
    participantsPage,
    setParticipantsPage,
    districtsTotalPages,
    districtsPage,
    setDistrictsPage,
    officesTotalPages,
    officesPage,
    setOfficesPage,
    isLoading: !hasLoaded,
  };
};
